import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Section } from "@/components/settings/Section";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sparkles, Save, Lock } from "lucide-react";

const MODEL_HINTS = { openai: "gpt-4o-mini", anthropic: "claude-3-5-haiku-latest", gemini: "gemini-3.1-flash-lite", ollama: "llama3" };
const PROVIDER_LABELS = { openai: "OpenAI", anthropic: "Anthropic (Claude)", gemini: "Google (Gemini)", ollama: "Ollama (self-hosted)" };
const KEY_PROVIDERS = ["openai", "anthropic", "gemini"];

function normalize(ai) {
  ai = ai || {};
  return {
    enabled: !!ai.enabled,
    activeProvider: ai.activeProvider || "openai",
    models: { openai: "", anthropic: "", gemini: "", ollama: "", ...(ai.models || {}) },
    keys: { openai: "", anthropic: "", gemini: "", ...(ai.keys || {}) },
    keysSet: ai.keysSet || {},
    openaiBaseUrl: ai.openaiBaseUrl || "",
    ollamaBaseUrl: ai.ollamaBaseUrl || "",
  };
}

export function AiSection({ s, onSaved }) {
  const { t } = useLang();
  const { demoMode, ai: aiInfo } = useAuth();
  const [ai, setAi] = useState(() => normalize(s.ai));
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  useEffect(() => { setAi(normalize(s.ai)); }, [s.ai]);
  const loadUsage = () => api.get("/ai/usage").then((r) => setUsage(r.data)).catch(() => {});
  useEffect(() => { loadUsage(); }, []);
  const resetUsage = async () => {
    try { await api.post("/ai/usage/reset"); await loadUsage(); toast.success(t("ai.usage.reset.done")); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const licensed = aiInfo?.licensed ?? true;
  const managed = !!aiInfo?.managed;
  const provList = ["openai", "anthropic", "gemini"].concat(managed ? [] : ["ollama"]);
  const p = ai.activeProvider;
  const isKeyProv = KEY_PROVIDERS.includes(p);

  const set = (patch) => setAi((a) => ({ ...a, ...patch }));
  const setModel = (v) => setAi((a) => ({ ...a, models: { ...a.models, [p]: v } }));
  const setKey = (v) => setAi((a) => ({ ...a, keys: { ...a.keys, [p]: v } }));

  const configured = isKeyProv ? !!(ai.keys[p] && ai.keys[p].length > 0) : !!ai.ollamaBaseUrl;
  const statusLabel = !ai.enabled ? t("ai.set.statusDisabled") : (configured ? t("ai.set.statusReady") : t("ai.set.statusNeedsConfig"));
  const statusCls = !ai.enabled ? "border-zinc-200 bg-zinc-100 text-zinc-500" : (configured ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700");

  const save = async () => {
    if (demoMode) { toast.info(t("demo.settingsNoSave")); return; }
    setSaving(true);
    try {
      const keys = {};
      KEY_PROVIDERS.forEach((k) => { const v = ai.keys[k]; if (v && !v.includes("•")) keys[k] = v; });
      const body = { ai: {
        enabled: ai.enabled, activeProvider: ai.activeProvider,
        models: {
          openai: ai.models.openai || MODEL_HINTS.openai, anthropic: ai.models.anthropic || MODEL_HINTS.anthropic,
          gemini: ai.models.gemini || MODEL_HINTS.gemini, ollama: ai.models.ollama || MODEL_HINTS.ollama,
        },
        openaiBaseUrl: ai.openaiBaseUrl || "", ollamaBaseUrl: ai.ollamaBaseUrl || "", keys,
      } };
      const { data } = await api.put("/settings", body);
      onSaved?.(data);
      toast.success(t("ai.set.saved"));
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Section icon={Sparkles} title={t("ai.set.title")}>
      <p className="mb-4 text-sm text-zinc-500">{t("ai.set.desc")}</p>
      {!licensed && (
        <div className="mb-4 flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700" data-testid="ai-not-licensed">
          <Lock size={14} /> {t("ai.set.notLicensed")}
        </div>
      )}
      <fieldset disabled={!licensed} className={!licensed ? "opacity-60" : ""}>
        <label className="flex items-center justify-between border-b border-zinc-100 pb-4">
          <div>
            <div className="text-sm font-bold text-zinc-900">{t("ai.set.enable")}</div>
            <div className="text-sm text-zinc-500">{t("ai.set.enableHint")}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusCls}`} data-testid="ai-status-badge">{statusLabel}</span>
            <Switch checked={ai.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="ai-enable-switch" />
          </div>
        </label>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ai.set.provider")}</Label>
            <Select value={p} onValueChange={(v) => set({ activeProvider: v })}>
              <SelectTrigger data-testid="ai-provider-select" className="rounded-sm border-zinc-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                {provList.map((pr) => (<SelectItem key={pr} value={pr} data-testid={`ai-provider-${pr}`}>{PROVIDER_LABELS[pr]}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ai.set.model")}</Label>
            <Input value={ai.models[p] || ""} onChange={(e) => setModel(e.target.value)} placeholder={MODEL_HINTS[p]} data-testid="ai-model-input" className="rounded-sm border-zinc-300 font-mono text-sm" />
            <p className="text-[11px] text-zinc-400">{t("ai.set.modelHint")} <span className="font-mono">{MODEL_HINTS[p]}</span></p>
          </div>
        </div>

        {isKeyProv && (
          <div className="mt-4 space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ai.set.apiKey")} — {PROVIDER_LABELS[p]}</Label>
            <Input type="password" value={ai.keys[p] || ""} onChange={(e) => setKey(e.target.value)} placeholder="sk-…" data-testid="ai-key-input" className="rounded-sm border-zinc-300 font-mono text-sm" />
            {ai.keysSet?.[p] && <p className="text-[11px] text-zinc-400">{t("ai.set.keyStored")}</p>}
          </div>
        )}
        {p === "openai" && (
          <div className="mt-4 space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ai.set.openaiBase")}</Label>
            <Input value={ai.openaiBaseUrl || ""} onChange={(e) => set({ openaiBaseUrl: e.target.value })} placeholder="https://api.openai.com/v1" data-testid="ai-openai-base" className="rounded-sm border-zinc-300 font-mono text-sm" />
            <p className="text-[11px] text-zinc-400">{t("ai.set.openaiBaseHint")}</p>
          </div>
        )}
        {p === "ollama" && !managed && (
          <div className="mt-4 space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ai.set.ollamaBase")}</Label>
            <Input value={ai.ollamaBaseUrl || ""} onChange={(e) => set({ ollamaBaseUrl: e.target.value })} placeholder="http://localhost:11434" data-testid="ai-ollama-base" className="rounded-sm border-zinc-300 font-mono text-sm" />
            <p className="text-[11px] text-zinc-400">{t("ai.set.ollamaHint")}</p>
          </div>
        )}

        <div className="mt-5 flex justify-end border-t border-zinc-100 pt-4">
          <Button onClick={save} disabled={saving || !licensed} data-testid="ai-save-button" className="rounded-sm">
            <Save size={15} className="mr-2" /> {saving ? t("common.saving") : t("ai.set.save")}
          </Button>
        </div>

        <div className="mt-6 border-t border-zinc-100 pt-4" data-testid="ai-usage-panel">
          <div className="mb-1 flex items-center justify-between">
            <div className="text-sm font-bold text-zinc-900">{t("ai.usage.title")}</div>
            {usage?.total > 0 && (
              <button type="button" onClick={resetUsage} data-testid="ai-usage-reset" className="text-[11px] font-semibold text-zinc-400 transition-colors hover:text-red-600">{t("ai.usage.reset")}</button>
            )}
          </div>
          <p className="mb-3 text-[11px] text-zinc-400">{t("ai.usage.desc")}</p>
          {(!usage || usage.total === 0) ? (
            <div className="text-sm text-zinc-400" data-testid="ai-usage-empty">{t("ai.usage.none")}</div>
          ) : (
            <div className="space-y-1.5">
              {["openai", "anthropic", "gemini", "ollama"].filter((pr) => (usage.counts?.[pr] || 0) > 0).map((pr) => (
                <div key={pr} className="flex items-center justify-between text-sm" data-testid={`ai-usage-${pr}`}>
                  <span className="text-zinc-600">{PROVIDER_LABELS[pr]}</span>
                  <span className="font-mono text-zinc-900">
                    {usage.counts[pr]}
                    {usage.lastUsed?.[pr] && <span className="ml-2 text-[11px] font-sans text-zinc-400">{t("ai.usage.lastUsed")} {new Date(usage.lastUsed[pr]).toLocaleDateString()}</span>}
                  </span>
                </div>
              ))}
              <div className="mt-2 flex items-center justify-between border-t border-zinc-100 pt-2 text-sm font-bold">
                <span className="text-zinc-700">{t("ai.usage.total")}</span>
                <span className="font-mono text-primary" data-testid="ai-usage-total">{usage.total}</span>
              </div>
            </div>
          )}
        </div>
      </fieldset>
    </Section>
  );
}
